import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminSarHelpApi } from '../../lib/api';
import { SARStatusPill } from '../../components/StatusPills';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/**
 * The support queue for requests someone has flagged with "Help me with
 * this" on their SAR page (routers/sar.py POST /sar/{id}/help). Nothing
 * here is decrypted for the purpose — a SAR's letter and correspondence
 * were never end-to-end encrypted in the first place (see admin_sar.py's
 * module docstring); this is exactly what the user themselves sees.
 */
const AdminSarHelp = () => {
  const [resolved, setResolved] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminSarHelpApi
      .list(resolved)
      .then((res) => !cancelled && setRows(res.data?.results || []))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [resolved]);

  return (
    <div className="space-y-6" data-testid="admin-sar-help">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.03em] text-ink">SAR help queue</h1>
          <p className="text-ink-70 mt-1">
            Requests someone has asked for help with — stuck, overdue with no clear next step, or a
            reply that didn't make sense.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant={resolved ? 'secondary' : 'default'}
            size="sm"
            onClick={() => setResolved(false)}
            data-testid="sar-help-tab-open"
          >
            Open
          </Button>
          <Button
            variant={resolved ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setResolved(true)}
            data-testid="sar-help-tab-resolved"
          >
            Resolved
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-ink-55">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-55" data-testid="sar-help-empty">
          {resolved ? 'Nothing resolved yet.' : 'Nothing waiting for help right now.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organisation</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Asked</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`sar-help-row-${r.id}`}>
                <TableCell className="font-medium text-ink">
                  {r.organisation_name}
                  {r.kind !== 'initial' && <span className="text-ink-55"> (follow-up)</span>}
                </TableCell>
                <TableCell>
                  <p className="text-ink">{r.user_name || 'Unknown'}</p>
                  <p className="text-sm text-ink-55">{r.user_email}</p>
                </TableCell>
                <TableCell>
                  <SARStatusPill status={r.status} daysRemaining={r.days_remaining} size="sm" />
                </TableCell>
                <TableCell className="text-ink-70">{fmt(r.help_requested_at)}</TableCell>
                <TableCell className="text-ink-70 max-w-[28ch] truncate" title={r.help_reason || ''}>
                  {r.help_reason || '—'}
                </TableCell>
                <TableCell>
                  <Link
                    to={`/admin/sar-help/${r.id}`}
                    className="text-sm font-semibold text-brand hover:underline underline-offset-4"
                    data-testid={`sar-help-open-${r.id}`}
                  >
                    Open →
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default AdminSarHelp;
